'use client';

import { Box, Container, IconButton, Stack, Typography } from '@mui/material';
import { useLocale } from 'next-intl';
import Image from 'next/image';

import Facebook from '@/components/SvgIcons/Socials/Facebook';
import LinkedIn from '@/components/SvgIcons/Socials/LinkedIn';
import X from '@/components/SvgIcons/Socials/X';
import { getTranslatedBlogSlug } from '@/config/slugs.config';
import colors from '@/styles/themes/colors';
import { Locale } from '@/types/language.type';

import styles from './SingleBlogHeroSection.module.scss';

interface SingleBlogHeroSectionProps {
  title: string;
  bannerImage: string;
  cardImage: string;
  slug: string;
}

const SingleBlogHeroSection = ({ title, bannerImage, cardImage, slug }: SingleBlogHeroSectionProps) => {
  const locale = useLocale() as Locale;
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://workspace.hr';
  const localizedSlug = getTranslatedBlogSlug(slug, locale, 'en');
  const shareUrl = locale === 'en' ? `${baseUrl}/blog/${localizedSlug}` : `${baseUrl}/hr/blog/${localizedSlug}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const socialLinks = [
    {
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      label: 'Share on Facebook',
    },
    {
      icon: X,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      label: 'Share on X',
    },
    {
      icon: LinkedIn,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      label: 'Share on LinkedIn',
    },
  ];

  return (
    <Container component="section" maxWidth={false} classes={{ root: styles.root }} className={styles.container}>
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          spacing={3}
          className={styles.header}
        >
          <Typography
            component="h1"
            color={colors.white}
            sx={{ typography: { xs: 'h4', lg: 'h3' } }}
            maxWidth={900}
            fontWeight={400}
          >
            {title}
          </Typography>
          <Stack direction="row" spacing={1} className={styles.socials}>
            {socialLinks.map(({ label, href, icon: Icon }) => (
              <IconButton
                key={label}
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className={styles.socialButton}
              >
                <Icon />
              </IconButton>
            ))}
          </Stack>
        </Stack>
        <Box className={styles.imageWrapper}>
          <Image src={cardImage} alt={title} fill className={styles.cardImage} priority />
          <Image src={bannerImage} alt={title} fill className={styles.bannerImage} priority />
        </Box>
      </Container>
    </Container>
  );
};

export default SingleBlogHeroSection;
